import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import { useFeature } from 'flagged';
import {
  addTaskIcon,
  giftIcon,
  interactionIcon,
  personIcon,
} from '@/assets/images';
import { ReactComponent as OpportunitiesIcon } from '@/assets/images/icons/opportunities-emoji.svg';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useContributionDialog from '@/components/common/contribution/useContributionDialog';
import { CustomDialogContent } from '@/components/common/CustomDialog';
import InteractionDialog from '@/components/common/interaction/InteractionDialog';
import MultiActionDialogKey from '@/constants/MultiActionDialogKey';
import FeatureFlags from '@/constants/system/FeatureFlags';
import CreateEditDialogType from '@/constants/ui/CreateEditDialogType';
import useEntity from '@/lib/hooks/swr/useEntity';
import translate from '@/lib/intl/translate';
import { AddNewConstituentModalContent } from '@/modules/ConstituentDetails';
import { useCreateEditOpportunityDialog } from '@/modules/Opportunities';
import {
  CreateEditTaskDialog,
  ModalType,
  useTaskAnalytics,
} from '@/modules/Tasks';
import userActionTypes from '@/rdx/actionTypes';
import type ActionDialogData from '@/types/ActionDialogData';
import type RootState from '@/types/RootState';
import type Task from '@/types/Task';
import { showErrorMessage, showSuccessMessage } from '@/utils/ToastFeedback';
import { useStyles } from './MultiActionDialog.styles';
import MultiActionDialogButton from './MultiActionDialogButton';

type MultiActionDialogProps = DialogProps & {
  handleClose: () => void;
  onEntityCreated: () => void;
};

function MultiActionDialog({
  open,
  handleClose,
  onEntityCreated,
}: MultiActionDialogProps) {
  const { constituentId } = useParams<{ constituentId: string }>();
  const history = useHistory();
  const classes = useStyles();
  const hasOpportunity = useFeature(FeatureFlags.OPPORTUNITIES_MILESTONE_1);
  const { sendGABCreatedAction } = useTaskAnalytics();

  const { actionDialog } = useSelector((state: RootState) => ({
    actionDialog: state.actionDialog,
  }));
  const { remove } = useEntity(
    (actionDialog.data as ActionDialogData)?.contentId,
    actionDialog.config?.entityType,
  );
  const dispatch = useDispatch();

  const handleActionClick = (dialogKey: MultiActionDialogKey) => {
    // Close the primary modal
    handleClose();
    dispatch({ type: userActionTypes.OPEN_ACTION_DIALOG, dialogKey });
  };

  const onActionDialogClose = () => {
    dispatch({ type: userActionTypes.CLOSE_ACTION_DIALOG });
  };

  const onActionDialogSuccess = () => {
    onActionDialogClose();
    onEntityCreated();
  };

  // Use DialogManagerProvider to open the contribution form
  useContributionDialog(
    actionDialog?.dialogKey === MultiActionDialogKey.DONATION,
    {
      preSelectedDonorId:
        actionDialog.data?.constituents?.[0]?.constituentId || constituentId,
      donationId: (actionDialog.data as ActionDialogData)?.donationId,
      onClose: onActionDialogClose,
      onSuccess: onActionDialogSuccess,
    },
  );
  useCreateEditOpportunityDialog(
    actionDialog?.dialogKey === MultiActionDialogKey.ADD_OPPORTUNITY,
    {
      preSelectedConstituentId: constituentId,
      onClose: onActionDialogClose,
      type: CreateEditDialogType.CREATE,
    },
  );

  const ActionDialogContent = React.useMemo(() => {
    switch (actionDialog.dialogKey) {
      case MultiActionDialogKey.ADD_CONSTITUENT:
        return (
          <AddNewConstituentModalContent handleClose={onActionDialogClose} />
        );
      case MultiActionDialogKey.ADD_TASK:
        return (
          <CreateEditTaskDialog
            constituentId={
              (actionDialog.data as ActionDialogData)?.constituentId ||
              constituentId
            }
            onClose={onActionDialogClose}
            onSaveSuccess={sendGABCreatedAction}
            type={ModalType.CREATE}
          />
        );
      case MultiActionDialogKey.EDIT_TASK:
        return (
          <CreateEditTaskDialog
            onClose={onActionDialogClose}
            type={ModalType.EDIT}
            task={actionDialog.data as Task}
          />
        );
      case MultiActionDialogKey.INTERACTION:
        return (
          <InteractionDialog
            data={actionDialog.data}
            preSelectedDonorId={
              (actionDialog.data as ActionDialogData)?.constituentId ||
              constituentId
            }
            onClose={onActionDialogClose}
            onSuccess={onActionDialogSuccess}
          />
        );
      case MultiActionDialogKey.DELETE_FEED_ITEM_CONFIRMATION:
        // TODO(jon): This should be pulled out and refactored, maybe combined
        // with DeleteContributionModal?
        return (
          <CustomDialogContent
            actionButtonLabel={translate('DELETE')}
            dialogHeader={actionDialog.config.title}
            handleActionButton={async () => {
              try {
                await remove();
                showSuccessMessage({
                  message: translate('DELETE_FEED_ENTITY_SUCCESS_MESSAGE', {
                    entity: actionDialog.config.entityType,
                  }),
                });
                onActionDialogClose();
                history.goBack();
              } catch (err) {
                showErrorMessage({
                  message: translate('DELETE_FEED_ENTITY_ERROR_MESSAGE', {
                    entity: actionDialog.config.entityType,
                  }),
                });
              }
            }}
            onClose={onActionDialogClose}
            showDialogActions
          >
            <Box my={{ xs: 0, sm: 1 }}>
              <Typography>{actionDialog.config.label}</Typography>
            </Box>
          </CustomDialogContent>
        );
      default:
        return null;
    }
  }, [actionDialog.dialogKey]);

  // Contribution form and Create Opportunity are using new
  // DialogManagerProvider to launch it, so don't render anything
  // below.
  if (
    actionDialog.dialogKey === MultiActionDialogKey.DONATION ||
    actionDialog.dialogKey === MultiActionDialogKey.ADD_OPPORTUNITY
  ) {
    return null;
  }

  return (
    <>
      <Dialog
        className={classes.mainDialog}
        data-testid="multi-action-dialog"
        onClose={handleClose}
        open={open && !actionDialog.open}
        scroll="paper"
      >
        <Box className={classes.container}>
          <Box className={classes.item}>
            <MultiActionDialogButton
              onClick={() =>
                handleActionClick(MultiActionDialogKey.ADD_CONSTITUENT)
              }
            >
              {translate('ADD_CONSTITUENT')}
            </MultiActionDialogButton>
            <Fab
              size="small"
              className={classes.buttonSmall}
              onClick={() =>
                handleActionClick(MultiActionDialogKey.ADD_CONSTITUENT)
              }
            >
              <img
                alt="add-donor-action"
                className={classes.icon}
                data-testid="add-donor-action"
                src={personIcon}
              />
            </Fab>
          </Box>
          <Box className={classes.item}>
            <MultiActionDialogButton
              data-testid="add-new-contribution"
              id="add-new-contribution"
              onClick={() => handleActionClick(MultiActionDialogKey.DONATION)}
            >
              {translate('ADD_CONTRIBUTION')}
            </MultiActionDialogButton>
            <Fab
              size="small"
              className={classes.buttonSmall}
              onClick={() => handleActionClick(MultiActionDialogKey.DONATION)}
            >
              <img
                alt="add-donation-action"
                className={classes.icon}
                data-testid="add-donation-action"
                src={giftIcon}
              />
            </Fab>
          </Box>
          {hasOpportunity && (
            <Box className={classes.item}>
              <MultiActionDialogButton
                data-testid="add-new-opportunity"
                id="add-new-opportunity"
                onClick={() =>
                  handleActionClick(MultiActionDialogKey.ADD_OPPORTUNITY)
                }
              >
                {translate('ADD_OPPORTUNITY')}
              </MultiActionDialogButton>
              <Fab
                size="small"
                className={classes.buttonSmall}
                onClick={() =>
                  handleActionClick(MultiActionDialogKey.ADD_OPPORTUNITY)
                }
              >
                <OpportunitiesIcon className={classes.icon} />
              </Fab>
            </Box>
          )}
          <Box className={classes.item}>
            <MultiActionDialogButton
              onClick={() =>
                handleActionClick(MultiActionDialogKey.INTERACTION)
              }
            >
              {translate('ADD_INTERACTION')}
            </MultiActionDialogButton>
            <Fab
              size="small"
              className={classes.buttonSmall}
              onClick={() =>
                handleActionClick(MultiActionDialogKey.INTERACTION)
              }
            >
              <img
                alt="add-interaction-action"
                className={classes.icon}
                data-testid="add-interaction-action"
                src={interactionIcon}
              />
            </Fab>
          </Box>
          <div className={classes.item}>
            <MultiActionDialogButton
              onClick={() => handleActionClick(MultiActionDialogKey.ADD_TASK)}
            >
              {translate('ADD_TASK')}
            </MultiActionDialogButton>
            <Fab
              size="small"
              className={classes.buttonSmall}
              onClick={() => handleActionClick(MultiActionDialogKey.ADD_TASK)}
            >
              <img
                alt=""
                className={classes.icon}
                data-testid="add-task-action"
                src={addTaskIcon}
              />
            </Fab>
          </div>
          <Box className={classes.item}>
            <MultiActionDialogButton
              className="pr-3"
              data-testid="close-multi-action-dialog"
              onClick={handleClose}
            >
              {translate('MULTI_ACTION_CLOSE')}
            </MultiActionDialogButton>
            <Fab className={classes.button} onClick={handleClose}>
              <Close color="primary" />
            </Fab>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        className={classes.contentDialog}
        fullWidth
        maxWidth="md"
        open={actionDialog.open}
        onClose={onActionDialogClose}
        scroll="paper"
      >
        {ActionDialogContent}
      </Dialog>
    </>
  );
}

export default MultiActionDialog;
